/**
 * Impl dos use cases de Horas Complementares (coordenação).
 *
 * @since paridade SimpleAcademy — Horas Complementares (2026-07-15)
 */
import type { Page } from '@/domain/model/Page';
import type {
  HorasConfigGateway,
  HorasLancamentoGateway,
  ListHorasFilaParams,
} from '@/application/port/out/HorasComplementaresGateway';
import type {
  AprovarHorasCommand,
  HorasCategoria,
  HorasCategoriaCommand,
  HorasConfig,
  HorasConfigCommand,
  HorasLancamento,
  RejeitarHorasCommand,
} from '@/domain/model/HorasComplementares';
import type {
  AprovarHorasLancamentoUseCase,
  CreateHorasCategoriaUseCase,
  CreateHorasConfigUseCase,
  DeactivateHorasCategoriaUseCase,
  DeactivateHorasConfigUseCase,
  DownloadHorasComprovanteUseCase,
  GetHorasLancamentoUseCase,
  ListHorasCategoriasUseCase,
  ListHorasConfigsUseCase,
  ListHorasFilaUseCase,
  RejeitarHorasLancamentoUseCase,
  UpdateHorasCategoriaUseCase,
  UpdateHorasConfigUseCase,
} from '@/application/port/in/HorasComplementaresUseCase';

export class ListHorasConfigs implements ListHorasConfigsUseCase {
  constructor(private readonly g: HorasConfigGateway) {}
  execute(): Promise<HorasConfig[]> { return this.g.listConfigs(); }
}
export class CreateHorasConfig implements CreateHorasConfigUseCase {
  constructor(private readonly g: HorasConfigGateway) {}
  execute(cmd: HorasConfigCommand): Promise<HorasConfig> { return this.g.createConfig(cmd); }
}
export class UpdateHorasConfig implements UpdateHorasConfigUseCase {
  constructor(private readonly g: HorasConfigGateway) {}
  execute(id: string, cmd: HorasConfigCommand): Promise<HorasConfig> { return this.g.updateConfig(id, cmd); }
}
export class DeactivateHorasConfig implements DeactivateHorasConfigUseCase {
  constructor(private readonly g: HorasConfigGateway) {}
  execute(id: string): Promise<void> { return this.g.deactivateConfig(id); }
}
export class ListHorasCategorias implements ListHorasCategoriasUseCase {
  constructor(private readonly g: HorasConfigGateway) {}
  execute(configId: string): Promise<HorasCategoria[]> { return this.g.listCategorias(configId); }
}
export class CreateHorasCategoria implements CreateHorasCategoriaUseCase {
  constructor(private readonly g: HorasConfigGateway) {}
  execute(configId: string, cmd: HorasCategoriaCommand): Promise<HorasCategoria> {
    return this.g.createCategoria(configId, cmd);
  }
}
export class UpdateHorasCategoria implements UpdateHorasCategoriaUseCase {
  constructor(private readonly g: HorasConfigGateway) {}
  execute(categoriaId: string, cmd: HorasCategoriaCommand): Promise<HorasCategoria> {
    return this.g.updateCategoria(categoriaId, cmd);
  }
}
export class DeactivateHorasCategoria implements DeactivateHorasCategoriaUseCase {
  constructor(private readonly g: HorasConfigGateway) {}
  execute(categoriaId: string): Promise<void> { return this.g.deactivateCategoria(categoriaId); }
}
export class ListHorasFila implements ListHorasFilaUseCase {
  constructor(private readonly g: HorasLancamentoGateway) {}
  execute(params: ListHorasFilaParams): Promise<Page<HorasLancamento>> { return this.g.fila(params); }
}
export class GetHorasLancamento implements GetHorasLancamentoUseCase {
  constructor(private readonly g: HorasLancamentoGateway) {}
  execute(id: string): Promise<HorasLancamento> { return this.g.get(id); }
}
export class AprovarHorasLancamento implements AprovarHorasLancamentoUseCase {
  constructor(private readonly g: HorasLancamentoGateway) {}
  execute(id: string, cmd: AprovarHorasCommand): Promise<HorasLancamento> { return this.g.aprovar(id, cmd); }
}
export class RejeitarHorasLancamento implements RejeitarHorasLancamentoUseCase {
  constructor(private readonly g: HorasLancamentoGateway) {}
  execute(id: string, cmd: RejeitarHorasCommand): Promise<HorasLancamento> { return this.g.rejeitar(id, cmd); }
}
export class DownloadHorasComprovante implements DownloadHorasComprovanteUseCase {
  constructor(private readonly g: HorasLancamentoGateway) {}
  execute(id: string): Promise<Blob> { return this.g.baixarComprovante(id); }
}
